/**
 * Règles de validation centralisées pour les formulaires Ant Design
 *
 * Ce fichier contient toutes les fonctions de validation réutilisables
 * pour garantir la cohérence des validations à travers l'application.
 */

// Pattern pour les noms, prénoms : lettres, accents, espaces, traits d'union, apostrophes
const NAME_PATTERN = /^[a-zA-ZÀ-ÿ\s'-]+$/;

// Pattern pour les fonctions : même règle que les noms
const FUNCTION_PATTERN = /^[a-zA-ZÀ-ÿ\s'-]+$/;

// Pattern pour le téléphone : formats français permissifs (0612345678, +33 6 12 34 56 78, etc.)
const PHONE_PATTERN = /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/;

// Pattern pour le code postal : exactement 5 chiffres
const ZIPCODE_PATTERN = /^[0-9]{5}$/;

// Pattern pour le mot de passe : min 8 caractères, majuscule, minuscule, chiffre, caractère spécial
const PASSWORD_PATTERN = /^\S*(?=\S{8,})(?=\S*[a-z])(?=\S*[A-Z])(?=\S*\d)(?=\S*[\W])\S*$/;

/**
 * Règles de validation pour les noms et prénoms
 * @param {string} fieldLabel - Libellé du champ (ex: "Le nom", "Le prénom")
 * @returns {Array} Tableau de règles Ant Design Form
 */
export const getNameRules = (fieldLabel = "Ce champ") => [
  {
    required: true,
    message: `${fieldLabel} est obligatoire`,
  },
  {
    pattern: NAME_PATTERN,
    message: `${fieldLabel} ne peut contenir que des lettres, espaces, traits d'union et apostrophes`,
  },
  {
    max: 100,
    message: `${fieldLabel} ne peut pas dépasser 100 caractères`,
  },
];

/**
 * Règles de validation pour les fonctions/postes
 * @param {string} fieldLabel - Libellé du champ (ex: "La fonction")
 * @returns {Array} Tableau de règles Ant Design Form
 */
export const getFunctionRules = (fieldLabel = "Ce champ") => [
  {
    required: true,
    message: `${fieldLabel} est obligatoire`,
  },
  {
    pattern: FUNCTION_PATTERN,
    message: `${fieldLabel} ne peut contenir que des lettres, espaces, traits d'union et apostrophes`,
  },
  {
    max: 100,
    message: `${fieldLabel} ne peut pas dépasser 100 caractères`,
  },
];

/**
 * Règles de validation pour les numéros de téléphone
 * @param {boolean} required - Le champ est-il obligatoire ?
 * @returns {Array} Tableau de règles Ant Design Form
 */
export const getPhoneRules = (required = true) => {
  const rules = [];

  if (required) {
    rules.push({
      required: true,
      message: "Le numéro de téléphone est obligatoire",
    });
  }

  rules.push({
    pattern: PHONE_PATTERN,
    message: "Le numéro de téléphone n'est pas valide (format français attendu)",
  });

  return rules;
};

/**
 * Règles de validation pour les emails
 * @param {boolean} required - Le champ est-il obligatoire ?
 * @returns {Array} Tableau de règles Ant Design Form
 */
export const getEmailRules = (required = true) => {
  const rules = [];

  if (required) {
    rules.push({
      required: true,
      message: "L'adresse email est obligatoire",
    });
  }

  rules.push({
    type: 'email',
    message: "L'adresse email n'est pas valide",
  });

  return rules;
};

/**
 * Règles de validation pour les codes postaux
 * @param {boolean} required - Le champ est-il obligatoire ?
 * @returns {Array} Tableau de règles Ant Design Form
 */
export const getZipcodeRules = (required = true) => {
  const rules = [];

  if (required) {
    rules.push({
      required: true,
      message: "Le code postal est obligatoire",
    });
  }

  rules.push({
    pattern: ZIPCODE_PATTERN,
    message: "Le code postal doit contenir exactement 5 chiffres",
  });

  return rules;
};

/**
 * Règles de validation pour les mots de passe
 * @returns {Array} Tableau de règles Ant Design Form
 */
export const getPasswordRules = () => [
  {
    required: true,
    message: "Le mot de passe est obligatoire",
  },
  {
    pattern: PASSWORD_PATTERN,
    message: "Le mot de passe doit contenir au moins 8 caractères dont une majuscule, une minuscule, un chiffre et un caractère spécial",
  },
];

/**
 * Règles de validation pour les champs texte simples obligatoires
 * @param {string} fieldLabel - Libellé du champ
 * @param {number} maxLength - Longueur maximale (défaut: 255)
 * @returns {Array} Tableau de règles Ant Design Form
 */
export const getRequiredTextRules = (fieldLabel = "Ce champ", maxLength = 255) => [
  {
    required: true,
    message: `${fieldLabel} est obligatoire`,
  },
  {
    max: maxLength,
    message: `${fieldLabel} ne peut pas dépasser ${maxLength} caractères`,
  },
];
